import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ProspectCard } from './ProspectCard';
import { type Prospect, type PipelineStage } from '@/hooks/useProspects';
import { PIPELINE_COLORS } from '@/lib/constants';

interface KanbanColumnProps {
  stage: PipelineStage;
  title: string;
  prospects: Prospect[];
  onEditProspect?: (prospect: Prospect) => void;
  onDeleteProspect?: (prospectId: string) => void;
}

export function KanbanColumn({ 
  stage, 
  title, 
  prospects, 
  onEditProspect, 
  onDeleteProspect 
}: KanbanColumnProps) {
  const { isOver, setNodeRef } = useDroppable({
    id: stage,
  });

  const prospectIds = prospects.map((p) => p._id);

  return (
    <Card 
      className={`h-full min-h-[500px] transition-colors ${
        isOver ? 'ring-2 ring-blue-500 ring-offset-2' : ''
      }`}
    >
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div 
              className={`w-3 h-3 rounded-full ${PIPELINE_COLORS[stage]}`} 
              aria-hidden="true"
            />
            {title}
          </div>
          <Badge variant="secondary" className="text-xs">
            {prospects.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      
      <CardContent 
        ref={setNodeRef}
        className="space-y-3 flex-1 min-h-0"
      >
        <SortableContext 
          items={prospectIds} 
          strategy={verticalListSortingStrategy}
        >
          {prospects.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground">
                No prospects in this stage
              </p>
            </div>
          ) : (
            <div className="space-y-3 group">
              {prospects.map((prospect) => (
                <ProspectCard
                  key={prospect._id}
                  prospect={prospect}
                  onEdit={onEditProspect}
                  onDelete={onDeleteProspect}
                />
              ))}
            </div>
          )}
        </SortableContext>
      </CardContent>
    </Card>
  );
}